import * as React from 'react';

import { translate } from '~l10n/client';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';

import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import { EventTypes } from '~analytics/types';
import { UIButton } from '~shared/components/buttons';
import { DialogConfirmation } from '~shared/components/Dialog';
import { toast } from '~shared/components/Toast';
import { selectedTeamIdAtom } from '~teacher/components/Account/atoms';
import { isSeatsAvailableDialogOpenAtom } from '~teacher/components/Account/TeamMembers/SeatsAvailableDialog';
import { selectedPublicIdsAtom } from '~teacher/components/Account/TeamMembers/TeamMembersTable/atoms';
import { useRemoveMembersFromTeam } from '~teacher/hook/team/useRemoveMembersFromTeam';

export const isRemoveFromTeamMemberDialogOpenAtom = atom<boolean>(false);

export function RemoveFromTeamDialog() {
  const onClose = () => {
    resetSelectedPublicId();
    setIsOpen(false);
  };

  const [isOpen, setIsOpen] = useAtom(isRemoveFromTeamMemberDialogOpenAtom);

  const selectedPublicId = useAtomValue(selectedPublicIdsAtom.valueAtom);
  const resetSelectedPublicId = useSetAtom(selectedPublicIdsAtom.resetAtom);
  const useSelectedTeamId = useAtomValue(selectedTeamIdAtom);
  const setIsSeatAvailableDialogOpen = useSetAtom(
    isSeatsAvailableDialogOpenAtom
  );

  const [isLoading, setIsLoading] = React.useState(false);

  const removeMemberFromTeam = useRemoveMembersFromTeam();

  const handleRemoveFromTeam = () => {
    setIsLoading(true);

    removeMemberFromTeam.mutate(
      {
        teamId: useSelectedTeamId,
        publicIds: Array.from(selectedPublicId),
      },
      {
        onSuccess: () => {
          setIsLoading(false);
          onClose();
          setIsSeatAvailableDialogOpen(true);

          sendTeacherAmplitudeEvent(EventTypes.TeamsRemoveMembers, {
            team_id: useSelectedTeamId,
          });
        },
        onError: (error) => {
          if (__DEV__) {
            console.error(error);
          }

          toast.critical(translate('Unknown Error. Please try again later.'));
          setIsLoading(false);
        },
      }
    );
  };

  return (
    <DialogConfirmation
      actions={[
        <UIButton key="cancel" isDisabled={isLoading} onClick={onClose}>
          {translate('Cancel')}
        </UIButton>,
        <UIButton
          appearance="risky"
          key="remove"
          isLoading={isLoading}
          onClick={handleRemoveFromTeam}
        >
          {translate('Remove')}
        </UIButton>,
      ]}
      open={isOpen}
      onClose={onClose}
      id="remove-team-member"
      title={translate('Remove From Team')}
    >
      {translate('Are you sure you want to remove this member from the team?')}
    </DialogConfirmation>
  );
}
